import React from 'react';
import { Grid, Typography, Box } from '@mui/material';
// import { InputField } from '../../../../../../components/formFields/InputField';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import InputField from '../../../../../components/formFields/InputField';
import CheckboxField from '../../../../../components/formFields/checkBoxField';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { Alert, Switch, FormControlLabel } from '@mui/material';

interface Props {
    isViewMode: boolean;
}

export const ConstraintsForm = ({ isViewMode }: Props) => {
    const { values, setFieldValue } = useFormikContext<any>();
    const { globalRules } = useSelector((state: any) => state.positionSchedulingRules);

    const hasGlobalRules = !!globalRules;

    const globalMaxHours = globalRules?.maxHorasSemanales ?? '-';
    const globalRestHours = globalRules?.horasParaDescanso ?? '-';
    const globalRotating = globalRules?.permitirTurnosRotativos === 1;

    // Derived inheritance state (if value is empty or null, it's inheriting)
    const inheritMaxHours = hasGlobalRules && !values.constraints?.maxWeeklyHours;
    const inheritRestHours = hasGlobalRules && !values.constraints?.minRestHoursBetweenShifts;
    const inheritRotating = hasGlobalRules && (values.constraints?.allowRotatingShifts === '' || values.constraints?.allowRotatingShifts === null || values.constraints?.allowRotatingShifts === undefined);

    return (
        <Grid container spacing={3}>
            {!hasGlobalRules && (
                <Grid size={{ xs: 12 }}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        No hay reglas globales configuradas en el sistema. Debes ingresar valores específicos para cada restricción, o ir al módulo de Reglas Globales para configurar valores por defecto.
                    </Alert>
                </Grid>
            )}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(105, 108, 255, 0.02)' }, transition: 'all 0.2s' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <AccessTimeOutlinedIcon color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Máximo de Horas Semanales
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            Establece el límite superior de horas que el empleado puede trabajar en una semana natural antes de generar alertas de sobretiempo.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={inheritMaxHours}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setFieldValue('constraints.maxWeeklyHours', '');
                                        } else {
                                            setFieldValue('constraints.maxWeeklyHours', globalMaxHours !== '-' ? globalMaxHours : 0);
                                        }
                                    }}
                                    disabled={isViewMode || !hasGlobalRules}
                                    color="secondary"
                                />
                            }
                            label={<Typography variant="caption" fontWeight="bold">Heredar ({globalMaxHours}h)</Typography>}
                            labelPlacement="start"
                        />
                    </Box>
                    <InputField
                        name="constraints.maxWeeklyHours" 
                        label="Horas Máximas" 
                        type="number"
                        disabled={isViewMode || inheritMaxHours}
                        placeholder={inheritMaxHours ? `Global: ${globalMaxHours}` : "Ingrese máximo de horas"}
                    />
                </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(105, 108, 255, 0.02)' }, transition: 'all 0.2s' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <BedtimeOutlinedIcon color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Descanso Mínimo entre Turnos (Horas)
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            Garantiza un período de descanso legal o adecuado entre la finalización de un turno y el inicio del siguiente.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={inheritRestHours}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setFieldValue('constraints.minRestHoursBetweenShifts', '');
                                        } else {
                                            setFieldValue('constraints.minRestHoursBetweenShifts', globalRestHours !== '-' ? globalRestHours : 0);
                                        }
                                    }}
                                    disabled={isViewMode || !hasGlobalRules}
                                    color="secondary"
                                />
                            }
                            label={<Typography variant="caption" fontWeight="bold">Heredar ({globalRestHours}h)</Typography>}
                            labelPlacement="start"
                        />
                    </Box>
                    <InputField 
                        name="constraints.minRestHoursBetweenShifts" 
                        label="Horas de descanso" 
                        type="number"
                        disabled={isViewMode || inheritRestHours}
                        placeholder={inheritRestHours ? `Global: ${globalRestHours}` : "Ingrese descanso mínimo"}
                    />
                </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(105, 108, 255, 0.02)' }, transition: 'all 0.2s' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <DateRangeOutlinedIcon color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Máximo de Días Consecutivos Trabajados
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Evita que el personal trabaje demasiados días seguidos sin al menos un día libre (ej. 6 días).
                    </Typography>
                    <InputField 
                        name="constraints.maxConsecutiveWorkDays" 
                        label="Días consecutivos (ej: 6)" 
                        type="number"
                        disabled={isViewMode}
                        placeholder="Sin límite"
                    />
                </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(105, 108, 255, 0.02)' }, transition: 'all 0.2s' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <ChangeCircleOutlinedIcon color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Permitir Turnos Rotativos
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            Autoriza al programador asignar turnos de diferentes franjas horarias a los empleados de este cargo.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={inheritRotating}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setFieldValue('constraints.allowRotatingShifts', '');
                                        } else {
                                            setFieldValue('constraints.allowRotatingShifts', globalRotating);
                                        }
                                    }}
                                    disabled={isViewMode || !hasGlobalRules}
                                    color="secondary"
                                />
                            }
                            label={<Typography variant="caption" fontWeight="bold">Heredar ({hasGlobalRules ? (globalRotating ? 'Sí' : 'No') : '-'})</Typography>}
                            labelPlacement="start"
                        />
                    </Box>
                    <CheckboxField 
                        name="constraints.allowRotatingShifts" 
                        label="Habilitar turnos rotativos para este cargo" 
                        disabled={isViewMode || inheritRotating}
                    />
                </Box>
            </Grid>
        </Grid>
    );
};
